import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface Group {
  id: string;
  name: string;
  description?: string;
  category?: string;
  is_private?: boolean;
  current_members?: number;
  max_members?: number;
  status?: string;
  created_at?: string;
  media_count?: number;
  docs_count?: number;
  posts_count?: number;
  admin_names?: string[];
  is_joined?: boolean;
  isJoined?: boolean;
}

interface GroupAdminRow {
  group_id: string;
  user?: {
    full_name?: string | null;
  } | null;
}

interface GroupDashboardCountsRow {
  group_id: string;
  posts_count: number;
  media_count: number;
  docs_count: number;
}

interface GroupMembershipRow {
  group_id: string;
}

export const useGroups = (currentUserId?: string) => {
  return useQuery<Group[]>({
    queryKey: ['groups', currentUserId ?? null],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const groups = (data ?? []) as Group[];
      if (!groups.length) return [];

      const groupIds = groups.map((group) => group.id);
      const { data: countRows, error: countError } = await supabase.rpc(
        'get_group_dashboard_counts',
        { p_group_ids: groupIds }
      );

      if (countError) throw countError;

      const { data: adminRows, error: adminError } = await supabase
        .from('group_members')
        .select('group_id, user:users(full_name)')
        .eq('role', 'admin')
        .in('group_id', groupIds);

      if (adminError) throw adminError;

      let membershipRows: GroupMembershipRow[] = [];
      if (currentUserId) {
        const { data: membershipData, error: membershipError } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', currentUserId)
          .in('group_id', groupIds);

        if (membershipError) throw membershipError;
        membershipRows = (membershipData ?? []) as GroupMembershipRow[];
      }

      const adminNames = new Map<string, string[]>();
      for (const row of (adminRows ?? []) as GroupAdminRow[]) {
        const groupId = row.group_id;
        const name = row.user?.full_name?.trim();
        if (!groupId || !name) continue;
        const names = adminNames.get(groupId) ?? [];
        if (!names.includes(name)) {
          names.push(name);
        }
        adminNames.set(groupId, names);
      }

      const groupCounts = new Map<string, { posts: number; media: number; docs: number }>();
      for (const row of (countRows ?? []) as GroupDashboardCountsRow[]) {
        const groupId = row.group_id;
        if (!groupId) continue;
        groupCounts.set(groupId, {
          posts: row.posts_count ?? 0,
          media: row.media_count ?? 0,
          docs: row.docs_count ?? 0,
        });
      }

      const joinedGroupIds = new Set(membershipRows.map((row) => row.group_id));

      return groups.map((group) => {
        const counts = groupCounts.get(group.id) ?? { posts: 0, media: 0, docs: 0 };
        const isJoined = joinedGroupIds.has(group.id);
        return {
          ...group,
          posts_count: counts.posts,
          media_count: counts.media,
          docs_count: counts.docs,
          admin_names: adminNames.get(group.id) ?? [],
          is_joined: isJoined,
          isJoined,
        };
      });
    },
  });
};
